import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, Grid, Paper, Avatar, Button, Card, CardContent, LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Badge, Tooltip, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileImageUpload from '../components/ProfileImageUpload';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, orderBy, limit, DocumentData, Timestamp } from 'firebase/firestore';

// Genbruger styled components fra AgentDashboard
const AppContainer = styled(Box)({
  display: 'flex',
  height: '100%',
  backgroundColor: '#f5f5f5'
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: '150px',
  backgroundColor: 'white',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
}));

const MenuItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MenuItemText = styled(Typography)({
  fontSize: '0.85rem',
  color: '#666',
});

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  backgroundColor: '#f5f5f5',
  overflow: 'auto',
  height: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  color: '#334155',
  padding: theme.spacing(1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

// Race Track Styled Components
const RaceTrack = styled(Paper)(({ theme }) => ({
  position: 'relative',
  height: '250px',
  background: 'linear-gradient(to right, #1a237e, #283593, #3949ab, #3f51b5, #5c6bc0, #7986cb)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.1)',
}));

const RaceTrackOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
  pointerEvents: 'none',
});

const RaceTrackGlow = styled(Box)({
  position: 'absolute',
  top: 0,
  right: '10px',
  height: '100%',
  width: '100px',
  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
  pointerEvents: 'none',
});

const RaceTrackLine = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  height: '3px',
  background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.7) 100%)',
  transform: 'translateY(-50%)',
  boxShadow: '0 0 10px rgba(255,255,255,0.5)',
});

const TrackMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  width: '3px',
  height: '16px',
  background: 'rgba(255,255,255,0.8)',
  transform: 'translateY(-50%)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-15px',
    left: '-17px',
    width: '40px',
    height: '40px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
    borderRadius: '50%',
  }
}));

const MarkerLabel = styled(Typography)({
  position: 'absolute',
  top: '65%',
  transform: 'translateX(-50%)',
  color: 'rgba(255,255,255,0.8)',
  fontSize: '0.75rem',
  fontWeight: 'bold',
});

const FinishLine = styled(Box)({
  position: 'absolute',
  top: 0,
  right: '30px',
  height: '100%',
  width: '6px',
  background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9) 10px, rgba(0,0,0,0.6) 10px, rgba(0,0,0,0.6) 20px)',
  boxShadow: '0 0 15px rgba(255,255,255,0.7)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-15px',
    height: '100%',
    width: '15px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 100%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: '-15px',
    height: '100%',
    width: '15px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)',
  }
});

const FinishFlag = styled(Box)({
  position: 'absolute',
  top: '10px',
  right: '36px',
  color: 'white',
  fontSize: '26px',
  textShadow: '0 0 8px rgba(255,255,255,0.7)',
});

const GoalLabel = styled(Typography)({
  position: 'absolute',
  top: '10px',
  right: '36px',
  transform: 'translateX(120%)',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '1.1rem',
  textShadow: '0 2px 5px rgba(0,0,0,0.5)',
});

const RunnerAvatar = styled(Avatar)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 60,
  height: 60,
  border: '3px solid #fff',
  transition: 'left 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
  zIndex: 2,
  boxShadow: '0 0 15px rgba(0,0,0,0.4)',
  '&:hover': {
    transform: 'translate(-50%, -50%) scale(1.1)',
    boxShadow: '0 0 20px rgba(255,255,255,0.6)',
    zIndex: 10,
  }
}));

const RunnerName = styled(Typography)({
  position: 'absolute',
  top: '-45px',
  transform: 'translateX(-50%)',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '0 1px 3px rgba(0,0,0,0.7)',
  whiteSpace: 'nowrap',
  backgroundColor: 'rgba(0,0,0,0.5)',
  padding: '2px 8px',
  borderRadius: '10px',
  zIndex: 3,
});

const ScoreBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '10px',
  transform: 'translateX(-50%)',
  backgroundColor: theme.palette.primary.dark,
  color: 'white',
  padding: '3px 8px',
  borderRadius: '12px',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.3)',
}));

const AvatarGlow = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)',
  filter: 'blur(5px)',
  zIndex: 1,
  transition: 'left 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
}));

const LeaderboardCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const ImprovementBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#4caf50',
    color: 'white',
  },
}));

// TypeScript interfaces for Firebase data
interface FirebaseUser extends DocumentData {
  name?: string;
  email?: string;
  user_id?: string;
  role?: string;
  display_name?: string;
  profileImageUrl?: string;
  team?: string;
}

interface VoiceScoreData extends DocumentData {
  user_id: string;
  voice_score: number;
  timestamp: any; // Kan være string eller Timestamp
}

// Interface til at beskrive brugerdata for race
interface RaceUserData {
  id: string;
  user_id: string;
  name: string;
  email: string;
  score: number;
  rank: number;
  avatarUrl?: string;
  team?: string;
  improvement: number;
  oldestScore: number;
}

// Hjælpefunktion til at logge objekt-data mere læsbart
const logObject = (title: string, obj: any) => {
  console.log(title, JSON.stringify(obj, null, 2));
};

const Race: React.FC = () => {
  const { currentUser, userName } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<'race' | 'improvement' | 'challenge'>('race');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [raceUsers, setRaceUsers] = useState<RaceUserData[]>([]);
  const [currentUserData, setCurrentUserData] = useState<RaceUserData | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");

  useEffect(() => {
    const fetchRaceData = async () => {
      try {
        setLoading(true);
        setError(null);
        let debug = "";
        
        console.log("===== STARTER DATAHENTNING =====");
        
        if (!currentUser) {
          console.error("Ingen bruger logget ind");
          setError("Du skal være logget ind for at se race-data");
          setLoading(false);
          return;
        }
        
        // 1. Find den aktuelle brugers team først
        console.log("Henter den aktuelle brugers profil...");
        const currentUserEmail = currentUser.email;
        const usersCollection = collection(db, 'users');
        const currentUserQuery = query(usersCollection, where('email', '==', currentUserEmail));
        const currentUserSnapshot = await getDocs(currentUserQuery);
        
        if (currentUserSnapshot.empty) {
          console.error("Kunne ikke finde den aktuelle bruger i databasen");
          setError("Din brugerprofil blev ikke fundet");
          setLoading(false);
          return;
        }
        
        const currentUserData = currentUserSnapshot.docs[0].data() as FirebaseUser;
        const currentUserTeam = currentUserData.team;
        
        debug += `Bruger team: ${currentUserTeam || "intet team"}, `;
        
        if (!currentUserTeam) {
          console.log("Bruger har ikke noget team, viser alle brugere");
        } else {
          console.log(`Bruger tilhører team: ${currentUserTeam}`);
        }
        
        // 2. Hent brugere enten fra samme team eller alle brugere hvis ingen team
        let usersQuery;
        if (currentUserTeam) {
          console.log(`Filtrerer brugere baseret på team`);
          usersQuery = query(usersCollection, where('team', '==', currentUserTeam));
        } else {
          console.log("Henter alle brugere (ingen team-filtrering)");
          usersQuery = query(usersCollection);
        }
        
        const usersSnapshot = await getDocs(usersQuery);
        console.log(`Antal brugere fundet: ${usersSnapshot.size}`);
        debug += `Fandt ${usersSnapshot.size} brugere, `;
        
        if (usersSnapshot.empty) {
          setError("Ingen brugerdata fundet. Tilføj nogle voice scores for at se race-data.");
          setDebugInfo(debug);
          setLoading(false);
          return;
        }
        
        // Mapper user dokumenter til array
        const users: FirebaseUser[] = [];
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data() as FirebaseUser;
          users.push({
            id: doc.id,
            ...userData
          });
          
          // Log hver bruger for debugging
          console.log(`BRUGER: ID=${doc.id}, ${userData.name || 'Unavngivet'}, user_id=${userData.user_id || 'MANGLER'}, role=${userData.role || 'MANGLER'}`);
        });
        
        console.log(`Fandt ${users.length} brugere`);
        
        // 3. Hent ALLE voice scores fra rule_result_agent collection 
        console.log("Henter voice scores fra rule_result_agent...");
        const allCallsCollection = collection(db, 'rule_result_agent');
        const allCallsSnapshot = await getDocs(allCallsCollection);
        
        console.log(`Antal dokumenter i rule_result_agent collection: ${allCallsSnapshot.size}`);
        debug += `Fandt ${allCallsSnapshot.size} voice score dokumenter, `;
        
        // Bygger array med race brugere
        const racers: RaceUserData[] = [];
        let usersWithScoresCount = 0;
        let managementUsersSkipped = 0;
        
        // 4. Nu behandler vi hver bruger
        console.log("Starter behandling af brugere...");
        
        for (const user of users) {
          try {
            const userId = user.user_id || "";
            const userName = user.name || user.display_name || 'Unavngivet bruger';
            console.log(`Behandler bruger: ${userName} (user_id: ${userId || "MANGLER"}), role: ${user.role || 'ingen'}`);
            
            // Tjek om brugeren har rolle "Management" - disse skal springes over
            // Men vi inkluderer altid den aktuelle bruger
            const isCurrentUser = user.email === currentUserEmail;
            const isManagementRole = user.role?.toLowerCase() === "management";
            
            if (isManagementRole && !isCurrentUser) {
              console.log(`Springer bruger over - ${userName} har rollen ${user.role} (management)`);
              managementUsersSkipped++;
              continue; // Spring til næste bruger i loopet
            }
            
            let latestScore = 0;
            let oldestScore = 0;
            let improvementPercent = 0;
            
            // Hvis brugeren har et user_id, prøv at hente voice scores
            if (userId) {
              console.log(`Søger voice scores for bruger: ${userName} (${userId})`);
              
              // Filtrer alle dokumenter manuelt (mere pålideligt)
              const matchingDocs = allCallsSnapshot.docs.filter(doc => {
                const data = doc.data();
                return data.user_id && data.user_id.toString() === userId.toString();
              });
              
              console.log(`Fandt ${matchingDocs.length} voice score dokumenter for bruger ${userName}`);
              
              if (matchingDocs.length > 0) {
                // Sorter dokumenter efter timestamp (nyeste først)
                const sortedDocs = matchingDocs.sort((a, b) => {
                  const aTime = a.data().timestamp;
                  const bTime = b.data().timestamp;
                  
                  // Håndter forskellige timestamp-formater
                  let aTimeValue = 0;
                  let bTimeValue = 0;
                  
                  if (aTime && typeof aTime.toMillis === 'function') {
                    aTimeValue = aTime.toMillis();
                  } else if (typeof aTime === 'string') {
                    aTimeValue = new Date(aTime).getTime();
                  }
                  
                  if (bTime && typeof bTime.toMillis === 'function') {
                    bTimeValue = bTime.toMillis();
                  } else if (typeof bTime === 'string') {
                    bTimeValue = new Date(bTime).getTime();
                  }
                  
                  return bTimeValue - aTimeValue; // Nyeste først
                });
                
                if (sortedDocs.length > 0) {
                  // Nyeste score
                  const latestData = sortedDocs[0].data();
                  if (latestData.voice_score !== undefined) {
                    latestScore = Number(latestData.voice_score);
                    console.log(`Fandt nyeste voice score ${latestScore} for bruger ${userName}`);
                  }
                  
                  // Ældste score for udvikling
                  if (sortedDocs.length > 1) {
                    const oldestData = sortedDocs[sortedDocs.length - 1].data();
                    if (oldestData.voice_score !== undefined) {
                      oldestScore = Number(oldestData.voice_score);
                      console.log(`Fandt ældste voice score ${oldestScore} for bruger ${userName}`);
                    }
                  }
                }
                
                // Beregn forbedringsprocent hvis vi har både ældste og nyeste score
                if (oldestScore > 0 && latestScore > 0) {
                  improvementPercent = Math.round(((latestScore - oldestScore) / oldestScore) * 100);
                  // Hvis forbedringen er negativ, viser vi 0 (ingen forbedring)
                  improvementPercent = Math.max(0, improvementPercent);
                  console.log(`Beregnet forbedring for ${userName}: ${improvementPercent}% (${oldestScore} -> ${latestScore})`);
                }
                
                usersWithScoresCount++;
              } else {
                console.log(`Ingen voice scores fundet for bruger ${userName}`);
              }
            } else {
              console.log(`Bruger mangler user_id: ${userName}`);
            }
            
            // Tilføj brugeren til race-listen SELVOM der ikke blev fundet scores
            const racer: RaceUserData = {
              id: user.id || 'unknown-id',
              user_id: userId || 'unknown-user-id',
              name: userName,
              email: user.email || '',
              score: latestScore,
              rank: 0, // Tilføjes senere
              avatarUrl: user.profileImageUrl,
              team: user.team, // Gemmer team for intern brug
              improvement: improvementPercent,
              oldestScore: oldestScore || 0
            };
            
            console.log(`Tilføjer bruger til race: ${racer.name}, score=${racer.score}, improvement=${racer.improvement}%`);
            racers.push(racer);
            
            // Hvis dette er den aktuelle bruger, gem separat reference
            if (user.email === currentUserEmail) {
              console.log(`Dette er den aktuelle bruger: ${racer.name}`);
              setCurrentUserData({...racer});
            }
          } catch (e) {
            console.error(`Fejl ved behandling af bruger:`, e);
          }
        }
        
        console.log(`Race brugere før sortering: ${racers.length}, brugere med scores: ${usersWithScoresCount}`);
        debug += `Tilføjet ${racers.length} brugere til race, skippet ${managementUsersSkipped} management-brugere, `;
        setDebugInfo(debug);
        
        // Sortér brugere efter score og tilføj rangering
        const sortedRacers = [...racers].sort((a, b) => b.score - a.score);
        sortedRacers.forEach((racer, index) => {
          racer.rank = index + 1;
          
          // Opdater også currentUserData med korrekt rangering
          if (racer.email === currentUserEmail) {
            setCurrentUserData({...racer});
          }
        });
        
        console.log("===== FÆRDIG MED DATAHENTNING =====");
        console.log(`Fandt ${sortedRacers.length} brugere med følgende placeringer:`);
        sortedRacers.forEach(racer => {
          console.log(`#${racer.rank}: ${racer.name} - Score: ${racer.score}, Improvement: ${racer.improvement}%`);
        });
        
        // Gem sorteret data
        setRaceUsers(sortedRacers);
        setLoading(false);
      } catch (error: any) {
        console.error("FATAL FEJL ved hentning af race data:", error);
        setError(`Fejl ved hentning af data: ${error.message}`);
        setDebugInfo(`Fejl: ${error.message}`);
        setLoading(false);
      }
    };
    
    fetchRaceData();
  }, [currentUser]);
  
  // Sortér efter score (race position)
  const sortedByScore = [...raceUsers].sort((a, b) => b.score - a.score);
  
  // Sortér efter forbedring (improvement)
  const sortedByImprovement = [...raceUsers].sort((a, b) => b.improvement - a.improvement);
  
  const handleNavigate = (path: string) => {
    console.log(`Navigerer til: ${path}`); // Debugging log
    if (path === 'Race') {
      navigate('/race');
    } else if (path === 'Calls') {
      navigate('/calls');
    } else if (path === 'Dashboard') {
      navigate('/agent-dashboard');
    } else if (path === 'Stats') {
      navigate('/stats');
    } else if (path === 'Training') {
      navigate('/training');
    } else {
      // Disse sider er endnu ikke implementeret
      console.log(`Side '${path}' er ikke implementeret endnu`);
    }
  };

  // Race Tab Content
  const renderRaceTrack = () => (
    <RaceTrack elevation={3}>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 'bold', textShadow: '0 2px 5px rgba(0,0,0,0.5)', mb: 3 }}>
        {t('race.raceTo100')}
      </Typography>
      
      <RaceTrackOverlay />
      <RaceTrackGlow />
      <RaceTrackLine />
      
      {/* Milepæls markører */}
      {[25, 50, 75].map(marker => (
        <React.Fragment key={marker}>
          <TrackMarker sx={{ left: `${marker}%` }} />
          <MarkerLabel sx={{ left: `${marker}%` }}>{marker}</MarkerLabel>
        </React.Fragment>
      ))}
      
      {/* Mållinje */}
      <FinishLine />
      <FinishFlag>🏁</FinishFlag>
      <GoalLabel>100</GoalLabel>
      
      {/* Løbere (avatarer med score) */}
      {sortedByScore.map(user => (
        <Box key={user.id} sx={{ position: 'relative' }}>
          <AvatarGlow sx={{ left: `${Math.min(user.score, 98)}%` }} />
          <RunnerAvatar 
            src={user.avatarUrl}
            alt={user.name}
            sx={{ 
              left: `${Math.min(user.score, 98)}%`,
              // Fremhæv den aktuelle bruger med en kant
              border: user.email === currentUser?.email ? '3px solid #ffeb3b' : '3px solid #fff'
            }}
          >
            {!user.avatarUrl && user.name.charAt(0).toUpperCase()}
          </RunnerAvatar>
          <ScoreBox sx={{ left: `${Math.min(user.score, 98)}%` }}>
            {user.score}
          </ScoreBox>
          <RunnerName sx={{ left: `${Math.min(user.score, 98)}%` }}>
            {user.name}
          </RunnerName>
        </Box>
      ))}
    </RaceTrack>
  );

  // Leaderboard Tab Content
  const renderLeaderboard = () => (
    <LeaderboardCard>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmojiEventsIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">{t('race.topScore')}</Typography>
        </Box>
        <List>
          {sortedByScore.slice(0, 5).map((user, index) => (
            <ListItem key={user.id} divider={index < 4}>
              <Typography variant="body2" sx={{ minWidth: '24px', fontWeight: 'bold' }}>
                #{index + 1}
              </Typography>
              <ListItemAvatar>
                <Avatar 
                  src={user.avatarUrl} 
                  alt={user.name}
                  sx={{ 
                    bgcolor: user.email === currentUser?.email ? 'primary.main' : undefined,
                  }}
                >
                  {!user.avatarUrl && user.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={user.name} 
                secondary={`${t('race.score')}: ${user.score}`} 
                primaryTypographyProps={{
                  fontWeight: user.email === currentUser?.email ? 'bold' : 'normal'
                }}
              />
              {user.improvement > 0 && (
                <Tooltip title={`${t('race.improvement')}: +${user.improvement}%`}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography color="success.main" variant="body2" sx={{ fontWeight: 'bold', mr: 0.5 }}>
                      +{user.improvement}%
                    </Typography>
                    <TrendingUpIcon color="success" fontSize="small" />
                  </Box>
                </Tooltip>
              )}
            </ListItem>
          ))}
        </List>
      </CardContent>
    </LeaderboardCard>
  );

  // Improvement Leaderboard
  const renderImprovementLeaderboard = () => (
    <LeaderboardCard>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">{t('race.biggestImprovement')}</Typography>
        </Box>
        <List>
          {sortedByImprovement.slice(0, 5).map((user, index) => (
            <ListItem key={user.id} divider={index < 4}>
              <Typography variant="body2" sx={{ minWidth: '24px', fontWeight: 'bold' }}>
                #{index + 1}
              </Typography>
              <ListItemAvatar>
                <Avatar 
                  src={user.avatarUrl} 
                  alt={user.name}
                  sx={{ 
                    bgcolor: user.email === currentUser?.email ? 'primary.main' : undefined,
                  }}
                >
                  {!user.avatarUrl && user.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={user.name} 
                secondary={
                  <>
                    <Typography component="span" variant="body2">{t('race.score')}: {user.score}</Typography>
                    {user.oldestScore > 0 && (
                      <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({t('race.from')} {user.oldestScore})
                      </Typography>
                    )}
                  </>
                }
                primaryTypographyProps={{
                  fontWeight: user.email === currentUser?.email ? 'bold' : 'normal'
                }}
              />
              <Box sx={{ minWidth: '80px', textAlign: 'right' }}>
                <Typography color="success.main" fontWeight="bold" variant="body1">
                  +{user.improvement}%
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </LeaderboardCard>
  );

  // Challenger Component
  const renderChallengeSection = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <GroupIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">{t('race.challengeColleague')}</Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('race.chooseColleague')}
        </Typography>
        <List>
          {sortedByScore
            .filter(user => user.email !== currentUser?.email) // Fjern den aktuelle bruger fra udfordringslisten
            .map((user, index) => (
            <ListItem key={user.id} divider={index < sortedByScore.length - 2}>
              <ListItemAvatar>
                <Avatar 
                  src={user.avatarUrl} 
                  alt={user.name}
                >
                  {!user.avatarUrl && user.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={user.name} 
                secondary={<>
                  {t('race.score')}: {user.score}
                  {user.improvement > 0 && (
                    <Typography component="span" color="success.main" variant="body2" sx={{ ml: 1 }}>
                      (+{user.improvement}%)
                    </Typography>
                  )}
                </>} 
              />
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => alert(`Udfordring sendt til ${user.name}`)}
              >
                {t('race.challenge')}
              </Button>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  return (
    <AppContainer>
      {/* Sidebar / Menu */}
      <Sidebar>
        <Box sx={{ padding: '10px', marginBottom: 2 }}>
          <ProfileImageUpload />
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Dashboard')} sx={{ marginTop: 1 }}>
          <MenuItemText>{t('common.dashboard')}</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Development</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Stats')}>
          <MenuItemText>Stats</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Calls')}>
          <MenuItemText>Calls</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Team</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Race')} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
          <MenuItemText>{t('race.title')}</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Training')}>
          <MenuItemText>{t('common.training')}</MenuItemText>
        </MenuItemContainer>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItemContainer onClick={() => handleNavigate('Help')}>
          <MenuItemText>Help</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Contact')}>
          <MenuItemText>Contact</MenuItemText>
        </MenuItemContainer>
      </Sidebar>
      
      {/* Main Content */}
      <MainContent>
        {/* Header */}
        <Header>
          <Typography variant="h6">{userName || "Voice Victor"}</Typography>
          <Typography variant="subtitle1">{t('race.title')}</Typography>
        </Header>
        
        {/* Content */}
        <ContentBox>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
              <CircularProgress />
            </Box>
          ) : error ? (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                {error}
              </Alert>
            </>
          ) : raceUsers.length === 0 ? (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('race.noUserData')}
              </Alert>
            </>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item>
                    <Button 
                      variant={selectedTab === 'race' ? 'contained' : 'outlined'} 
                      onClick={() => setSelectedTab('race')}
                    >
                      {t('race.raceTab')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button 
                      variant={selectedTab === 'improvement' ? 'contained' : 'outlined'} 
                      onClick={() => setSelectedTab('improvement')}
                    >
                      {t('race.improvementTab')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button 
                      variant={selectedTab === 'challenge' ? 'contained' : 'outlined'} 
                      onClick={() => setSelectedTab('challenge')}
                    >
                      {t('race.challengeTab')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Race Track Visualization */}
              {renderRaceTrack()}

              {/* Current User Stats if available */}
              {currentUserData && (
                <Card sx={{ mb: 3, borderLeft: '4px solid #1976d2' }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Avatar 
                          src={currentUserData.avatarUrl} 
                          alt={currentUserData.name}
                          sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
                        >
                          {!currentUserData.avatarUrl && currentUserData.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h6">{currentUserData.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('race.yourPosition', { rank: currentUserData.rank, total: raceUsers.length })}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box sx={{ textAlign: 'center', bgcolor: 'primary.main', color: 'white', p: 1, borderRadius: 2 }}>
                          <Typography variant="h4">{currentUserData.score}</Typography>
                          <Typography variant="body2">{t('race.voiceScore')}</Typography>
                        </Box>
                      </Grid>
                      {currentUserData.improvement > 0 && (
                        <Grid item>
                          <Box sx={{ textAlign: 'center', bgcolor: '#4caf50', color: 'white', p: 1, borderRadius: 2 }}>
                            <Typography variant="h4">+{currentUserData.improvement}%</Typography>
                            <Typography variant="body2">{t('race.improvement')}</Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Tab Content */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={selectedTab === 'challenge' ? 12 : 6}>
                  {selectedTab === 'race' && renderLeaderboard()}
                  {selectedTab === 'improvement' && renderImprovementLeaderboard()}
                  {selectedTab === 'challenge' && renderChallengeSection()}
                </Grid>
                
                {selectedTab !== 'challenge' && (
                  <Grid item xs={12} md={6}>
                    {renderChallengeSection()}
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </ContentBox>
      </MainContent>
    </AppContainer>
  );
};

export default Race; 